import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Wrench, Search, FileSpreadsheet, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/KpiCard";
import { FleetSummary } from "@/components/fleet/FleetSummary";
import { FleetTable } from "@/components/fleet/FleetTable";
import { VehicleDialog } from "@/components/fleet/VehicleDialog";
import { WorkshopsDialog } from "@/components/fleet/WorkshopsDialog";
import { FleetImportDialog } from "@/components/fleet/FleetImportDialog";
import { ArchiveDialog } from "@/components/fleet/ArchiveDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { stationColor } from "@/lib/format";

export default function FleetPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [meta, setMeta] = useState(null);
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ vehicles: [], incoming: [] });
  const [summary, setSummary] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [filters, setFilters] = useState({ contract: "all", status: "all", q: "" });
  const [editing, setEditing] = useState(null);
  const [showWorkshops, setShowWorkshops] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const loadWorkshops = () => api.get("/fleet/workshops").then((r) => setWorkshops(r.data));
  const load = useCallback(() => {
    api.get("/fleet/vehicles", { params: { station_id: isAdmin && stationId !== "all" ? stationId : undefined } }).then((r) => setData(r.data));
    api.get("/fleet/summary").then((r) => setSummary(r.data));
  }, [isAdmin, stationId]);

  useEffect(() => {
    api.get("/fleet/meta").then((r) => setMeta(r.data));
    api.get("/stations").then((r) => setStations(r.data));
    loadWorkshops();
  }, []);
  useEffect(load, [load]);

  const rows = useMemo(() => data.vehicles.filter((v) =>
    (filters.contract === "all" || v.contract_type === filters.contract) &&
    (filters.status === "all" || v.status === filters.status) &&
    (!filters.q || `${v.plate} ${v.model} ${v.provider} ${v.workshop}`.toLowerCase().includes(filters.q.toLowerCase()))), [data, filters]);

  if (!meta || !summary) return <p className="text-slate-500">Caricamento...</p>;
  const current = isAdmin ? (stationId === "all" ? summary.overall : summary.per_station.find((s) => s.station_id === stationId)) : summary.per_station[0];
  const title = isAdmin ? (stationId === "all" ? "Disponibilità furgoni" : `Disponibilità ${stations.find((s) => s.id === stationId)?.name || ""}`) : `Disponibilità ${stations[0]?.name || ""}`;
  const remove = async (v) => {
    if (!window.confirm(`Eliminare il mezzo ${v.plate}?`)) return;
    try { await api.delete(`/fleet/vehicles/${v.id}`); toast.success("Mezzo eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = () => { setEditing(null); load(); loadWorkshops(); };
  const deactivate = async (v) => {
    const reason = window.prompt(`Disattivare ${v.plate}? Il mezzo andrà nello storico.\nMotivo (facoltativo):`, "");
    if (reason === null) return;
    try { await api.post(`/fleet/vehicles/${v.id}/deactivate`, { reason }); toast.success(`${v.plate} spostato nello storico`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/fleet/export", { params: { station_id: isAdmin && stationId !== "all" ? stationId : undefined }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "disponibilita_furgoni.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const updated = summary.updated_at ? new Date(/[Z+]/.test(summary.updated_at.slice(19)) ? summary.updated_at : `${summary.updated_at}Z`).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle={`Situazione attuale dei mezzi · ultimo aggiornamento ${updated}`}>
        <Button variant="outline" onClick={() => setShowArchive(true)} data-testid="fleet-archive-button"><Archive className="mr-2 h-4 w-4" /> Storico</Button>
        <Button variant="outline" onClick={() => setShowWorkshops(true)} data-testid="fleet-workshops-button"><Wrench className="mr-2 h-4 w-4" /> Officine</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="fleet-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button variant="outline" onClick={() => setShowImport(true)} data-testid="fleet-import-button"><Upload className="mr-2 h-4 w-4" /> Importa Excel</Button>
        {isAdmin && <Button onClick={() => setEditing({ station_id: stationId !== "all" ? stationId : stations[0]?.id || "" })} disabled={!stations.length} data-testid="fleet-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo mezzo</Button>}
      </PageHeader>

      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="fleet-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((s) => (
            <button key={s.id} onClick={() => setStationId(s.id)} data-testid={`fleet-tab-${s.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === s.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {s.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, s.id) }} />}{s.name}
            </button>
          ))}
        </div>
      )}

      {current && <FleetSummary s={current} contracts={meta.contracts} />}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-9" placeholder="Cerca targa, modello, società, officina" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="fleet-search-input" /></div>
        <Select value={filters.contract} onValueChange={(v) => setFilters({ ...filters, contract: v })}>
          <SelectTrigger data-testid="fleet-filter-contract"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i contratti</SelectItem>{Object.entries(meta.contracts).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="fleet-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.entries(meta.statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <FleetTable rows={rows} meta={meta} showStation={isAdmin && stationId === "all"} canEdit isAdmin={isAdmin} onEdit={setEditing} onDelete={remove} onDeactivate={deactivate} />

      {data.incoming.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-base font-semibold md:text-lg">Mezzi ricevuti da altre sedi ({data.incoming.length})</h2>
          <FleetTable rows={data.incoming.map((v) => ({ ...v, __dest: true, status: v.dest_status || "DISPONIBILE", reason: v.dest_reason || "" }))} meta={meta} showStation canEdit isAdmin={false} onEdit={(v) => setEditing(v)} onDelete={remove} testId="fleet-incoming-table" />
        </div>
      )}

      <VehicleDialog vehicle={editing} isAdmin={isAdmin} stations={stations} meta={meta} workshops={workshops} onClose={() => setEditing(null)} onSaved={done} />
      <WorkshopsDialog open={showWorkshops} workshops={workshops} isAdmin={isAdmin} onClose={() => setShowWorkshops(false)} onChanged={loadWorkshops} />
      <ArchiveDialog open={showArchive} stationId={isAdmin && stationId !== "all" ? stationId : undefined} isAdmin={isAdmin} meta={meta} onClose={() => setShowArchive(false)} onChanged={load} />
      <FleetImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); done(); }} />
    </div>
  );
}
